<template>
  <div class="data-table">
    <BaseTable :data="displayData">
      <BaseElTableColumn :label="$t('externalTransaction.table.transactionId')" prop="serial" align="center" />
      <BaseElTableColumn :label="$t('externalTransaction.table.source')" prop="origin" align="center" />
      <BaseElTableColumn v-if="enableColumns.includes('customerProfile')" label="會員姓名 / 手機" prop="customerProfile" align="center" />
      <BaseElTableColumn :label="$t('externalTransaction.table.orderType')" prop="type" align="center" />
      <BaseElTableColumn v-if="options.showBranch" :label="$t('externalTransaction.table.branch')" prop="branch" align="center" />
      <BaseElTableColumn v-if="enableColumns.includes('branchName') && externalData.cyjPosEnabled" label="消費分店" prop="branchName" align="center" />
      <BaseElTableColumn v-if="enableColumns.includes('shopName')" :label="$t('externalTransaction.table.shop')" prop="shopName" align="center" />
      <BaseElTableColumn v-if="options.showPhone" :label="$t('externalTransaction.table.phone')" prop="phone" align="center" />
      <BaseElTableColumn :label="$t('externalTransaction.table.amount')" prop="amount" align="center" />
      <BaseElTableColumn :label="$t('externalTransaction.table.sellAmount')" prop="sellAmount" align="center" />
      <BaseElTableColumn :label="$t('externalTransaction.table.time')" prop="time" align="center" width="120px" />
      <BaseElTableColumn :label="$t('externalTransaction.table.isInvalid')" prop="isInvalid" align="center">
        <template slot-scope="scope">
          <Tag :type="scope.row.isInvalid ? 'error' : 'info'">{{ scope.row.isInvalid ? $t('externalTransaction.table.yes') : $t('externalTransaction.table.no') }}</Tag>
        </template>
      </BaseElTableColumn>
      <BaseElTableColumn :label="$t('externalTransaction.table.status')" prop="status" align="center" width="120px">
        <template slot-scope="scope">
          <StatusDisplay
            :is-tag="!!memberExternalTransactionStatus(scope.row.status, 'label')"
            tag-type="info"
            :display-text="memberExternalTransactionStatus(scope.row.status, 'label') ?? '-'"
          />
        </template>
      </BaseElTableColumn>
      <BaseElTableColumn :label="$t('externalTransaction.table.markedStatus')" prop="statusMarked" align="center" width="120px">
        <template slot-scope="scope">
          <StatusDisplay
            :is-tag="!!memberExternalTransactionMarkedStatus(scope.row.labels, 'label')"
            tag-type="info"
            :display-text="memberExternalTransactionMarkedStatus(scope.row.labels, 'label') ?? '-'"
          />
        </template>
      </BaseElTableColumn>
      <BaseElTableColumn :label="$t('externalTransaction.table.action')" fixed="right" width="110" align="center">
        <template slot-scope="scope">
          <TableEditBtnGroup
            hideDelete
            :editBtn="$t('externalTransaction.table.view')"
            @edit="onRowEdit(scope.row)"
          />
        </template>
      </BaseElTableColumn>
    </BaseTable>

    <ExternalTransactionRecordDetail
      v-if="show.detail"
      :record="selectRow"
      :find-record-api="findRecordApi || undefined"
      @close="show.detail = false"
      @refresh="$emit('refresh')"
      @goTransaction="openTransactionByReference"
    />
  </div>
</template>

<script>
import { defineComponent, computed, ref, reactive } from 'vue'
import ExternalTransactionRecordDetail from './ExternalTransactionRecordDetail.vue'
import BaseTable from '@/components/BaseTable.vue'
import { get, map, find, isEmpty } from 'lodash'
import { formatDate } from '@/utils/date'
import { originsConfig, memberExternalTransactionStatusConfig, markedStatusConfig, memberExternalTransactionTypeConfig } from '@/config/externalTransaction'
import { localeString } from '@/utils/helper'
import StatusDisplay from '@/components/Tag/StatusDisplay.vue'
import { useExternalTransactionResolver } from '@/use/useExternalTransactionResolver'

export default defineComponent({
  name: 'ExternalTransactionRecordTable',
  components: { BaseTable, ExternalTransactionRecordDetail, StatusDisplay },
  props: {
    tableData: { type: Array, defalt: () => [] },
    enableColumns: { type: Array, default: () => [] },
    externalData: { type: Object, default: () => ({}) },
    options: { type: Object, default: () => ({}) },
    fetchTransactionsApi: { type: Function, default: null },
    findRecordApi: { type: Function, default: null },
  },
  emits: ['refresh'],
  setup (props) {
    const { resolveExternalTransactionReference } = useExternalTransactionResolver({
      fetchTransactions: props.fetchTransactionsApi || undefined,
    })
    const selectRow = ref(null)
    const show = reactive({
      detail: false,
    })
    const modal = reactive({
      delete: false,
    })

    const findCyjPosBranch = (branchNo) => {
      if (!branchNo) return null
      return find(get(props.externalData, 'cyjPosBranchList', []), (branch) => {
        return get(branch, 'branchNo') === branchNo || get(branch, 'BranchNo') === branchNo
      })
    }

    const getCyjPosBranchName = (item) => {
      if (item.source !== originsConfig.cyjPos.value) return '-'

      const branchNo = get(item, 'customContext.cyjPos.branchNo') || item.BranchNo
      const branchData = findCyjPosBranch(branchNo)

      return get(branchData, 'branchName') || get(branchData, 'BranchName') || get(branchData, 'name') || '-'
    }

    const displayData = computed(() => {
      return map(props.tableData, (item) => {
        const customerName = item.customerName || get(item, 'Member.name') || item.memberName || '-'
        const phone = item.customerPhone || '-'
        const customerProfile = customerName && customerName !== '-'
          ? `${customerName} / ${phone}`
          : phone
        return {
          id: item.id,
          serial: item.transactionId,
          origin: get(originsConfig, `${item.source}.label`) || item.source,
          type: get(memberExternalTransactionTypeConfig, `${item.type}.label`, '一般訂單'),
          customerName,
          customerProfile,
          phone,
          amount: localeString(item.amount),
          sellAmount: localeString(item.sellAmount),
          time: formatDate(item.transactionTime),
          createdAt: formatDate(item.createdAt),
          isInvalid: item.isInvalid,
          transactionId: item.transactionId,
          items: item.items,
          status: item.status,
          shopName: get(find(get(props.externalData, 'shopList'), { id: item.ShopId }), 'name'),
          branchName: getCyjPosBranchName(item),
          branch: get(item, 'Branch.name', '-'),
          shopId: item.ShopId,
          labels: item.labels,
          paymentDetails: item.paymentDetails,
          transactionNote: item.transactionNote,
        }
      })
    })
    const memberExternalTransactionStatus = (key, attr) => {
      return get(memberExternalTransactionStatusConfig[key], attr)
    }
    const memberExternalTransactionMarkedStatus = (key, attr) => {
      if (isEmpty(key)) return null
      const voidLabels = key.some(label => label.key === 'manualInvalid' || label.key === 'systemInvalid')
      if (voidLabels) {
        return get(markedStatusConfig.manualInvalid, attr)
      }
    }
    const onRowEdit = (row) => {
      selectRow.value = row
      show.detail = true
    }

    const openTransactionByReference = async (reference) => {
      const resolvedRecord = await resolveExternalTransactionReference(reference)
      if (!resolvedRecord) return
      selectRow.value = resolvedRecord
      show.detail = true
    }

    return {
      modal,
      displayData,
      onRowEdit,
      openTransactionByReference,
      show,
      selectRow,
      memberExternalTransactionStatus,
      memberExternalTransactionMarkedStatus,
    }
  },
})
</script>
